import { defineStore } from "pinia";
import { api } from "@/app/services/api.service";

export interface InternalProcessesState {
  incorporation_name_check: any
  incorporation_name_check_error: any
  registration_name_check: any
  registration_name_check_error: any
  registration_service_actions: any
  registration_service_actions_pending: any
  registration_service_actions_failed: any
  incorporation_service_actions: any
  incorporation_service_actions_pending: any
  incorporation_service_actions_failed: any
  incorporation_process_stats: any
  building_society_name_check: any
  building_society_name_check_error: any
  reg_status_change: any
  doc_status_change: any
  status_change: any
  status_change_error: any
  reg_status_change_error: any
  doc_status_change_error: any
  documents_service_actions: any
  annual_returns_status_change: any
  approval_actions: any
  annual_returns_status_error: any
  form_compliance_status_change: any
  form_compliance_status_error: any
  success: any
  error: any
}

export interface ProcessFilters {
  statuses: string
  page: number
  limit: number
  search?: string
  startDate?: string
  endDate?: string
  types?: string
}

export const useInternalProcessesStore = defineStore('internalProcesses', {
  state: (): InternalProcessesState => ({
    incorporation_name_check: null,
    incorporation_name_check_error: null,
    registration_name_check: null,
    registration_name_check_error: null,
    registration_service_actions: null,
    registration_service_actions_pending: null,
    registration_service_actions_failed: null,
    incorporation_service_actions: null,
    incorporation_service_actions_pending: null,
    incorporation_service_actions_failed: null,
    incorporation_process_stats: null,
    building_society_name_check: null,
    building_society_name_check_error: null,
    reg_status_change: null,
    doc_status_change: null,
    status_change: null,
    status_change_error: null,
    reg_status_change_error: null,
    doc_status_change_error: null,
    documents_service_actions: null,
    annual_returns_status_change: null,
    approval_actions: null,
    annual_returns_status_error: null,
    form_compliance_status_change: null,
    form_compliance_status_error: null,
    success: null,
    error: null,
  }),

  getters: {
    getIncorporationNameCheck: (state) => state.incorporation_name_check,
    getIncorporationNameCheckError: (state) => state.incorporation_name_check_error,
    getRegistrationNameCheck: (state) => state.registration_name_check,
    getRegistrationNameCheckError: (state) => state.registration_name_check_error,
    getBuildingSocietyNameCheck: (state) => state.building_society_name_check,
    getBuildingSocietyNameCheckError: (state) => state.building_society_name_check_error,
    getRegistrationProcessStream: (state) => state.registration_service_actions,
    getDocumentsProcessStream: (state) => state.documents_service_actions,
    getApprovalActionsStream: (state) => state.approval_actions,
    getAnnualReturnsStatusChange: (state) => state.annual_returns_status_change,
    getAnnualReturnsStatusError: (state) => state.annual_returns_status_error,
    getFormComplianceStatusChange: (state) => state.form_compliance_status_change,
    getFormComplianceStatusError: (state) => state.form_compliance_status_error,
    getIncorporationProcessStats: (state) => state.incorporation_process_stats,
    getRegistrationProcessStreamPending: (state) => state.registration_service_actions_pending,
    getRegistrationProcessStreamFailed: (state) => state.registration_service_actions_failed,
    getIncorporationProcessStream: (state) => state.incorporation_service_actions,
    getIncorporationProcessStreamPending: (state) => state.incorporation_service_actions_pending,
    getIncorporationProcessStreamFailed: (state) => state.incorporation_service_actions_failed,
    getStatusChangeSuccess: (state) => state.status_change,
    getRegistrationStatusChangeSuccess: (state) => state.reg_status_change,
    getRegistrationStatusChangeError: (state) => state.reg_status_change_error,
    getDocumentStatusChangeSuccess: (state) => state.doc_status_change,
    getDocumentStatusChangeError: (state) => state.doc_status_change_error,
    getStatusChangeError: (state) => state.status_change_error,
    getInternalProcessStreamError: (state) => state.error,
  },

  actions: {
    async incorporationPushToNameCheck(data: any) {
      try {
        const response = await api().post('/incorporation/companies/registry/name-search/manual-update', data)
        this.incorporation_name_check = response.data
      } catch (error: any) {
        this.incorporation_name_check_error = error.response?.data
      }
    },

    async buildingSocietyPushToNameCheck(data: any) {
      try {
        const response = await api().post(`/building-societies/societies/registry/${data}/manual-publish`, data)
        this.building_society_name_check = response.data
      } catch (error: any) {
        this.building_society_name_check_error = error.response?.data
      }
    },

    async registrationPushToNameCheck(data: any) {
      try {
        const response = await api().post('/business-registration/sync-name-check', data)
        this.registration_name_check = response.data
      } catch (error: any) {
        this.registration_name_check_error = error.response?.data
      }
    },

    async fetchRegistrationProcessesStream(data: ProcessFilters) {
      try {
        let path = '/business-registration/sync-items?status=' + data.statuses + '&page=' + data.page + '&per_page=' + data.limit
        if (data.search !== '') {
          path += '&search=' + data.search
        }
        if (data.startDate) {
          path += '&start_date=' + data.startDate
        }
        if (data.endDate) {
          path += '&end_date=' + data.endDate
        }
        const response = await api().get(path)
        this.registration_service_actions = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchRegistrationProcessesPending() {
      try {
        const path = '/business-registration/sync-items?status=Pending&page=1&per_page=12'
        const response = await api().get(path)
        this.registration_service_actions_pending = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchRegistrationProcessesFailed() {
      try {
        const path = '/business-registration/sync-items?status=Failed&page=1&per_page=12'
        const response = await api().get(path)
        this.registration_service_actions_failed = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchIncorporationProcessesStream(data: ProcessFilters) {
      try {
        let path = "/incorporation/companies/get-all-actions?status=" + data.statuses + "&page=" + data.page + "&pageSize=" + data.limit;
        if (data.search !== '') {
          path += '&search=' + data.search
        }
        if (data.types) {
          path += '&type=' + data.types
        }
        if (data.startDate) {
          path += '&startDate=' + data.startDate
        }
        if (data.endDate) {
          path += '&endDate=' + data.endDate
        }
        const response = await api().get(path)
        this.incorporation_service_actions = response.data
      } catch (error: any) {
        console.error('API Error:', error.response)
        this.error = error.response?.data
      }
    },

    async fetchApprovalActions(data: any) {
      if (!data.actionId) {
        console.error('Invalid actionId:', data.actionId)
        this.error = { message: 'Invalid action ID provided' }
        throw new Error('Invalid action ID provided')
      }
      const path = `/incorporation/companies/approval-actions/${encodeURIComponent(data.actionId)}`;
      console.log('API Path:', path)
      try {
        const response = await api().get(path)
        console.log('API Response:', response.data)
        this.approval_actions = response.data
        return response
      } catch (error: any) {
        console.error('API Error:', error.response || error)
        this.error = error.response?.data || { message: 'Failed to fetch approval actions' }
        throw error
      }
    },

    async updateAnnualReturnStatus(data: any) {
      try {
        const response = await api().post("/incorporation/annual-returns/mark-years-as-filed/" + data.brn, data);
        this.annual_returns_status_change = response.data
      } catch (error: any) {
        this.annual_returns_status_error = error.response?.data
      }
    },

    async updateFormCompliance(data: any) {
      try {
        const response = await api().post("/incorporation/company-files/compliance-checks", data);
        this.form_compliance_status_change = response.data
      } catch (error: any) {
        this.form_compliance_status_error = error.response?.data
      }
    },

    async fetchIncorporationProcessStatistics() {
      try {
        const path = '/incorporation/companies/get-action_statistics'
        const response = await api().get(path)
        this.incorporation_process_stats = response.data.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchIncorporationProcessesPending() {
      try {
        const path = "/incorporation/companies/get-all-actions?status=Pending&page=1&pageSize=12";
        const response = await api().get(path)
        this.incorporation_service_actions_pending = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async fetchIncorporationProcessesFailed() {
      try {
        const path = "/incorporation/companies/get-all-actions?status=Expired&page=1&pageSize=12";
        const response = await api().get(path)
        this.incorporation_service_actions_failed = response.data
      } catch (error: any) {
        this.error = error.response?.data
      }
    },

    async updateProcessStatus(data: any) {
      this.status_change = null
      try {
        const response = await api().post("/incorporation/companies/update-action-status", data);
        this.status_change = response.data
      } catch (error: any) {
        this.status_change_error = error.response?.data
      }
    },

    async runProcessNow(data: any) {
      try {
        const response = await api().post("/incorporation/companies/run-action-immediately", data);
        this.status_change = response.data
      } catch (error: any) {
        this.status_change_error = error.response?.data
      }
    },

    async retryRegistrationProcess(data: any) {
      try {
        const path = '/business-registration/attachment/sync-item?brn=' + data.brn + '&sync-item=' + data.description
        const response = await api().post(path)
        this.reg_status_change = response.data
      } catch (error: any) {
        this.reg_status_change_error = error.response?.data
      }
    },

    async retryDocumentProcess(instrumentNumber: string) {
      try {
        const response = await api().post('/legal-documents/attachments/retry?instrumentNo=' + instrumentNumber)
        this.doc_status_change = response.data
      } catch (error: any) {
        this.doc_status_change_error = error.response?.data
      }
    },
  },
})
